/**
 * Shared photo-upload request builder (card T8u-BVfkbArp).
 *
 * ONE place assembles the multipart body for a lot/area/inventory photo, so the
 * two upload paths stay byte-for-byte identical:
 *
 *   - DIRECT (online): PhotoCapturePrimitive uploads the FRESH File the instant
 *     it is captured/picked. The File has intact bytes → the server receives a
 *     real file part (fixes the iOS "No file provided" 400).
 *   - QUEUED (offline / direct-upload network failure): the capture flow stores
 *     the image BYTES (ArrayBuffer) in IndexedDB — NOT a raw Blob, which WebKit
 *     returns EMPTY from IndexedDB on iOS — and the offline handler reconstructs
 *     a File from those bytes at replay time before calling this same builder.
 *
 * Both paths therefore build the SAME field set (file + organization_id +
 * lot_id + optional activity_id, with the area→parent-lot lookup and
 * `[Area: <name>]` caption tag). If the field set changes, it changes here once.
 *
 * NOTE (card 0cEzrRKvCW1D): user-facing per-photo captions were removed from the
 * field app. The `caption` form field is now used ONLY to carry the internal
 * `[Area: <name>]` tag that identifies area-targeted photos within a lot's
 * attachments — there is no user text appended anymore.
 */

import { httpClient } from '../../api/http-client.js'

/** Identifies what the photo attaches to. Mirrors PhotoTarget in the primitive. */
export type PhotoUploadTarget =
  | { kind: 'lot'; lotId: string; label?: string; activityId?: string }
  | { kind: 'area'; areaId: string; label?: string; activityId?: string }
  | { kind: 'inventory'; inventoryItemId: string; label?: string }

/** Everything needed to (re)build one photo-upload request. */
export interface PhotoUploadRequest {
  target: PhotoUploadTarget
  /**
   * Owning activity for a field-app completion photo (card 4_x_b5Smhsld).
   * Persisted to lot_attachments.activity_id for lot/area targets so the B1
   * photo-required gate + activity photo views scope to this activity.
   */
  activityId?: string
  /**
   * Area this field photo is assigned to (card eYaw2jj1_Bwx / F1). Persisted to
   * lot_attachments.area_id as the AUTHORITATIVE area link (the caption tag is
   * now a fallback only). For an 'area' target this is implied by the target
   * itself; for a 'lot' target the field app supplies the picked area here so a
   * multi-area activity's photos still each carry their area. REQUIRED for a
   * field photo — the server rejects a field upload (activity_id present) with
   * no area_id, and the capture UI blocks it at capture.
   */
  areaId?: string
  /** The image file. Fresh File (direct) or reconstructed File (queued). */
  file: File
}

/** Result of resolving/building the request. */
export interface BuiltPhotoUpload {
  url: string
  formData: FormData
}

async function getOrganizationId(): Promise<string | null> {
  // The /api/v1/auth/me endpoint returns the current user's profile. The photo
  // upload workflows expect organization_id in the form data; we resolve it here
  // so neither the direct call site nor the queue payload has to plumb it.
  try {
    const response = await httpClient.get<{
      data?: { organizationId?: string }
      user?: { organizationId?: string }
    }>('/auth/me')
    return (
      (response as any)?.data?.organizationId
      ?? (response as any)?.user?.organizationId
      ?? (response as any)?.organizationId
      ?? null
    )
  } catch {
    return null
  }
}

async function lookupAreaParent(
  areaId: string,
): Promise<{ lotId: string; areaName: string } | null> {
  try {
    const response = await httpClient.get<{ data: { lot_id: string; name: string }; success: boolean }>(
      `/areas/${areaId}`,
    )
    if (!response?.data?.lot_id) {
      return null
    }
    return { lotId: response.data.lot_id, areaName: response.data.name }
  } catch {
    return null
  }
}

/** Thrown when the request cannot be built (missing org / unresolved area). */
export class PhotoUploadBuildError extends Error {
  /** Whether retrying later could succeed (network/auth) vs a permanent error. */
  retryable: boolean
  constructor(message: string, retryable: boolean) {
    super(message)
    this.name = 'PhotoUploadBuildError'
    this.retryable = retryable
  }
}

/**
 * Assemble the multipart form + target URL for one photo. Shared by the direct
 * (online) and queued (offline replay) paths so both send an identical body.
 *
 * Throws PhotoUploadBuildError when org can't be resolved (retryable) or the
 * area's parent lot can't be found (non-retryable).
 */
export async function buildPhotoUpload(req: PhotoUploadRequest): Promise<BuiltPhotoUpload> {
  const { target, file } = req
  // Prefer the explicit activityId, fall back to the one carried on the target
  // (belt-and-suspenders — both are set by the capture flow).
  const activityId = req.activityId ?? ('activityId' in target ? target.activityId : undefined)
  // The area this photo is assigned to. An 'area' target IS the area; a 'lot'
  // target carries the picked area on req.areaId (multi-area activity).
  const areaId = req.areaId ?? (target.kind === 'area' ? target.areaId : undefined)

  const organizationId = await getOrganizationId()
  if (!organizationId) {
    throw new PhotoUploadBuildError(
      'Could not resolve organization for photo upload (auth)',
      true,
    )
  }

  const formData = new FormData()
  formData.append('file', file, file.name || `photo-${Date.now()}.jpg`)
  formData.append('organization_id', organizationId)
  // Attach the owning activity for lot/area completion photos. Not applicable to
  // inventory-photos, which upload to a different endpoint/table.
  if (activityId && target.kind !== 'inventory') {
    formData.append('activity_id', activityId)
  }
  // Attach the AUTHORITATIVE area link (card eYaw2jj1_Bwx). The server persists
  // this to lot_attachments.area_id; readers prefer it over the caption tag.
  if (areaId && target.kind !== 'inventory') {
    formData.append('area_id', areaId)
  }
  // A photo a field worker just took should be visible in the field app's
  // document views, which filter lot_attachments.field_visible = true (see
  // client/field/install/JobDocumentsList.tsx). The upload column defaults to
  // FALSE NOT NULL, so a field capture must opt in explicitly here; office (web)
  // uploaders omit this flag and keep the default (unchanged). Inventory photos
  // use a different endpoint/table and have no field_visible column.
  if (target.kind !== 'inventory') {
    formData.append('field_visible', 'true')
  }

  let url: string
  if (target.kind === 'lot') {
    url = '/api/v1/workflows/lot-attachments/upload'
    formData.append('lot_id', target.lotId)
    // A lot-target field photo that carries a picked area also gets the
    // `[Area: <name>]` caption tag (fallback signal), mirroring the area-target
    // path — resolve the area's name for the tag.
    if (areaId) {
      const area = await lookupAreaParent(areaId)
      if (area) {
        formData.append('caption', `[Area: ${area.areaName}]`)
      }
    }
  } else if (target.kind === 'area') {
    const parent = await lookupAreaParent(target.areaId)
    if (!parent) {
      throw new PhotoUploadBuildError(
        `Area ${target.areaId} not found — cannot resolve parent lot`,
        false,
      )
    }
    url = '/api/v1/workflows/lot-attachments/upload'
    formData.append('lot_id', parent.lotId)
    // PRESERVED (card 0cEzrRKvCW1D): the `[Area: <name>]` caption tag still
    // identifies area photos within the lot's attachments — used by
    // useAreaHasPhoto / AreaDetailPage to distinguish area-targeted uploads.
    // Now a FALLBACK signal behind lot_attachments.area_id (card eYaw2jj1_Bwx);
    // still written so pre-column rows + non-area readers keep working.
    formData.append('caption', `[Area: ${parent.areaName}]`)
  } else {
    url = '/api/v1/workflows/inventory-photos/upload'
    formData.append('inventory_item_id', target.inventoryItemId)
  }

  return { url, formData }
}

/** Outcome of a direct (online) upload attempt. */
export type DirectUploadResult =
  | { ok: true }
  | { ok: false; retryable: boolean; error: string }

/**
 * Build + POST the multipart body directly (used online). On a network error or
 * a retryable build failure, returns retryable:true so the caller can fall back
 * to the byte-safe offline queue instead of losing the photo.
 */
export async function uploadPhotoDirect(req: PhotoUploadRequest): Promise<DirectUploadResult> {
  let built: BuiltPhotoUpload
  try {
    built = await buildPhotoUpload(req)
  } catch (err) {
    if (err instanceof PhotoUploadBuildError) {
      return { ok: false, retryable: err.retryable, error: err.message }
    }
    return { ok: false, retryable: true, error: err instanceof Error ? err.message : String(err) }
  }

  try {
    const response = await fetch(built.url, {
      method: 'POST',
      credentials: 'include',
      body: built.formData,
    })
    if (!response.ok) {
      const text = await response.text().catch(() => response.statusText)
      const message = `HTTP ${response.status}: ${text}`
      if (response.status >= 400 && response.status < 500) {
        return { ok: false, retryable: false, error: message }
      }
      return { ok: false, retryable: true, error: message }
    }
    return { ok: true }
  } catch (err) {
    // Network failure (offline / dropped connection) — retryable → fall back to queue.
    return { ok: false, retryable: true, error: err instanceof Error ? err.message : String(err) }
  }
}

/**
 * Reconstruct a File from bytes stored in IndexedDB (the byte-safe offline
 * fallback). Raw Blobs come back EMPTY from IndexedDB on iOS Safari/PWA (WebKit
 * bug), so the queue stores an ArrayBuffer and we rebuild the File here at
 * replay time. The rebuilt File has intact bytes → a non-empty multipart part.
 */
export function fileFromBytes(bytes: ArrayBuffer, filename: string, mimeType: string): File {
  return new File([bytes], filename || `photo-${Date.now()}.jpg`, {
    type: mimeType || 'application/octet-stream',
  })
}
